/**
 * "Document" as the fourth view of a transaction.
 *
 * Offered only where a Document Form is actually ANCHORED on this object — the
 * same conditional shape the Process view uses, because a switcher must never
 * offer a view its own host cannot render. A transaction nobody has given a
 * document face never sees the option, and adding one is a model file, not a
 * change to the transaction.
 *
 * Everything the operator interacts with is `<DocumentFormView>` from
 * `@twasta/page-runtime/er`, so the designer's Live Preview, `/run` and a
 * downloaded copy of the application show the identical record. This hook owns
 * only the lookup ("is there one?") and the binding of the transport.
 */

import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react'
import { documentFormRuntime, documentFormsForObject } from '@/api/er'
import { makeDmsClient } from '@/api/dms'

/**
 * LAZY, and this is load-bearing rather than tidiness.
 *
 * `<DocumentFormView>` mounts the TipTap editor stack — StarterKit plus a
 * dozen extension packages and the whole `dms/authoring` schema. This hook is
 * imported by `LivePreviewTab` and `EmbedListInterfaceHost`, which are on the
 * path of EVERY transaction screen, so a static import would pull that stack
 * into the main graph for every user who never opens a document. It is also
 * what the DMS already does with the same editor (`DocumentRunPane`,
 * `RunView`) — a discipline worth keeping, not rediscovering: a static import
 * here is what made `vite build` run out of heap while rendering chunks.
 */
const DocumentFormView = lazy(async () => ({
  default: (await import('@twasta/page-runtime/er')).DocumentFormView,
}))

/**
 * The two things a document view reads from whatever is hosting it.
 *
 * Deliberately narrower than `TxnScreenInterfaceContext`, which structurally
 * satisfies it: the smart-page embed host has no TxnScreen around it and can
 * supply only these, and widening the parameter would force that call site
 * into a cast — a cast that would keep compiling the day the document starts
 * reading a third field it is not actually given.
 */
export interface DocumentViewContext {
  canEdit?: boolean
  /** The record to open the document ON. Absent ⇒ a blank one. */
  recordTxnId?: string | null
  /** Dismiss the host's record editor once the document has saved. */
  closeEditor?: () => void
  /** The screen's handle on the document's unsaved edits (its editor-layout
   *  switch asks Save / Discard before leaving the document). */
  documentControl?: { current: { dirty: boolean; save: () => Promise<boolean>; discard: () => void } | null }
}

export interface DocumentInterface {
  /** The `E` object bound to this transaction, or '' when there is none. */
  formObj: string
  /** Pass to `<TxnScreen documentView=…>`; undefined hides the switcher option. */
  documentView?: (ctx: DocumentViewContext) => React.ReactNode
}

export function useDocumentInterface(args: {
  project: string
  objName: string
  canEdit?: boolean
  onSaved?: () => void
  /**
   * Open the filed copy of a governed document in its repository.
   *
   * This is the answer to "the document view says it was filed — where?" from
   * the RECORD side: the fourth view is opened on one record, so the document
   * it shows is that record's, and the link leads to that record's controlled
   * copy. Only the Run shell can navigate, so only it supplies this.
   */
  onOpenFiled?: (args: { repositoryObj: string; documentId: string }) => void
}): DocumentInterface {
  const { project, objName, canEdit = true, onSaved, onOpenFiled } = args
  const [formObj, setFormObj] = useState('')

  useEffect(() => {
    let alive = true
    if (!project || !objName) { setFormObj(''); return }
    documentFormsForObject(project, objName)
      .then((forms) => {
        if (!alive) return
        // The FIRST anchored form. A transaction with two document faces is a
        // real thing (an invoice and a credit note over one object), and
        // choosing between them belongs on the switcher rather than here — but
        // offering nothing until that exists would be worse than offering the
        // first, which is what every other one-of-many affordance does.
        setFormObj(forms[0]?.obj_name || '')
      })
      .catch(() => {
        // A transaction whose document form cannot be listed simply has no
        // Document view. Never a toast: the viewer did not ask for one and
        // cannot act on it.
        if (alive) setFormObj('')
      })
    return () => { alive = false }
  }, [project, objName])

  const runtime = useMemo(
    () => (formObj ? documentFormRuntime(project, formObj) : null),
    [project, formObj])

  /**
   * So the parties can sign the filed copy HERE rather than being sent to the
   * repository. Built per repository because a document form's filed copy may
   * live in any `K` the type names, and memoised by project so the identity is
   * stable — an unstable factory would re-open the campaign on every render.
   */
  const dmsClientFor = useCallback(
    (repositoryObj: string) => (repositoryObj
      ? makeDmsClient(project, repositoryObj) : null),
    [project])

  return useMemo(() => {
    if (!formObj || !runtime) return { formObj: '' }
    return {
      formObj,
      documentView: (ctx: DocumentViewContext) => (
        <Suspense fallback={<DocumentLoading />}>
        <DocumentFormView
          project={project}
          objName={objName}
          formObj={formObj}
          runtime={runtime}
          txnId={ctx.recordTxnId || undefined}
          canEdit={canEdit && ctx.canEdit !== false}
          onSaved={() => { onSaved?.(); ctx.closeEditor?.() }}
          control={ctx.documentControl}
          onOpenFiled={onOpenFiled}
          dmsClientFor={dmsClientFor}
        />
        </Suspense>
      ),
    }
  }, [formObj, runtime, project, objName, canEdit, onSaved, onOpenFiled,
      dmsClientFor])
}

function DocumentLoading() {
  return (
    <div style={{
      padding: 24, fontSize: '0.9125rem', color: 'var(--color-text-secondary)',
    }}>Opening the document…</div>
  )
}
